/**
 * Единая точка сборки всех адресов бэкенда.
 *
 * Конфигурируется двумя значениями: хост и версия REST API.
 * Пустой хост означает «текущий origin» — так приложение работает,
 * когда /api и /ws проксируются тем же доменом, что и фронт.
 */

export const DEFAULT_API_VERSION = 'v1'

export interface EndpointsInput {
  /** Хост API, например `https://trade.example.com`. Пусто — используется `origin`. */
  host?: string
  /** Версия REST API, по умолчанию `v1`. */
  apiVersion?: string
  /** Origin, от которого работаем, если `host` не задан. */
  origin?: string
}

export interface Endpoints {
  /** Нормализованный origin API без завершающего слэша. */
  origin: string
  /** База REST API: `<origin>/api/<version>` */
  apiBase: string
  /** База WebSocket: `<ws-origin>/ws` */
  wsBase: string
  /** База UDF-датафида TradingView: `<apiBase>/market/udf` */
  udfBase: string
}

const stripSlashes = (value: string): string => value.replace(/^\/+|\/+$/g, '')

/** Приводит произвольный хост к виду `https://host` без завершающего слэша. */
export function normalizeOrigin(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, '')

  if (!trimmed) return ''
  if (/^wss:\/\//i.test(trimmed)) return `https://${trimmed.slice(6)}`
  if (/^ws:\/\//i.test(trimmed)) return `http://${trimmed.slice(5)}`
  if (/^https?:\/\//i.test(trimmed)) return trimmed

  return `https://${trimmed}`
}

/** `https://host` → `wss://host`, `http://host` → `ws://host`. */
export function toWebSocketOrigin(httpOrigin: string): string {
  if (/^https:\/\//i.test(httpOrigin)) return `wss://${httpOrigin.slice(8)}`
  if (/^http:\/\//i.test(httpOrigin)) return `ws://${httpOrigin.slice(7)}`

  return httpOrigin
}

export function resolveEndpoints({ host, apiVersion, origin }: EndpointsInput): Endpoints {
  const base = normalizeOrigin(host || '') || normalizeOrigin(origin || '')

  if (!base) {
    throw new Error(
      'Не удалось определить хост API: задайте NUXT_PUBLIC_API_HOST или вызывайте resolveEndpoints с origin'
    )
  }

  const version = stripSlashes(apiVersion || '') || DEFAULT_API_VERSION
  const apiBase = `${base}/api/${version}`

  return {
    origin: base,
    apiBase,
    wsBase: `${toWebSocketOrigin(base)}/ws`,
    udfBase: `${apiBase}/market/udf`
  }
}
